// L'APERÇU VIVANT : le document d'une page dont les scripts S'EXÉCUTENT.
//
// L'aperçu ordinaire (BrowserPane) n'exécute rien : parfait pour le HTML et le
// CSS, muet pour JavaScript. À partir de la section 7 du parcours JavaScript,
// une leçon porte des fichiers (`data.files`) et demande un aperçu vivant
// (`data.live`) : le `<script src="script.js">` de la page est résolu vers
// l'onglet du même nom, comme un `<link>` l'est déjà vers sa feuille de style.
//
// Ce module est du JavaScript PUR : le player du site s'en sert, les harnais
// Node aussi, et l'app en tient un miroir (src/features/lesson/live-page.ts).
//
// SÉCURITÉ. Le code vient d'un apprenant et ne doit rien pouvoir atteindre :
//   - sur le site, l'iframe est en sandbox="allow-scripts" SANS
//     allow-same-origin : origine opaque, donc ni cookies, ni stockage, ni
//     accès à la page de Reps ;
//   - une politique de contenu, écrite dans le document lui-même, coupe le
//     réseau : aucun fetch, aucun script distant, aucun formulaire envoyé
//     (`form-action 'none'` : `default-src` ne couvre PAS les formulaires).
//     Seules les images restent chargeables (les leçons en montrent).
//   - l'iframe a aussi `allow-forms` (section 15) : sans lui, le navigateur
//     ne déclenche même pas l'événement `submit`. L'envoi lui-même reste
//     bloqué par `form-action`.

import { normalizeFileName } from "./multifile.js";
import { protegerBoucles } from "./protegerBoucles.js";

export const LIVE_CSP =
    "default-src 'none'; script-src 'unsafe-inline' blob:; style-src 'unsafe-inline'; img-src https: data:; media-src https: data:; font-src data:; form-action 'none'";

// Un `</script>` écrit dans le code de l'apprenant fermerait notre balise.
function neutraliser(code) {
    return String(code == null ? "" : code).replace(/<\/script/gi, "<\\/script");
}

// La garde des boucles, ajoutée À LA FIN du script de l'apprenant (une
// déclaration de fonction est hissée : elle existe dès la première ligne). Elle
// doit vivre DANS ce script et non dans js-page.js : sous Safari, une erreur
// levée par un autre script de la page arrive masquée (« Script error. »), et
// la boucle arrêtée n'aurait plus ni nom ni ligne. Placée après le code, elle
// ne décale aucun numéro de ligne.
const GARDE =
    "\n;function __repsTour(l){if(__repsDepasse(l)){var e=new RangeError(__repsBoucle);e.__repsLigne=l;throw e}return true}";

/**
 * Résout les `<script src="…">` d'une page vers les fichiers `.js` connus.
 * Chaque balise résolue devient un appel `__repsCharger(n)` : js-page.js
 * charge alors le code depuis un blob, à cette place exacte. Rend le HTML
 * réécrit et la liste des scripts ({ nom, code }), code gardé compris.
 *
 * POURQUOI UN BLOB et pas le code collé dans la balise : dans une iframe isolée
 * (sandbox sans allow-same-origin), Safari masque les erreurs d'un script écrit
 * dans la page, et la console n'affichait que « Script error. », sans nom ni
 * ligne (mesuré sur WebKit le 14/10/2026). Un blob créé par la page elle-même
 * partage son origine : ses erreurs arrivent entières, et leurs numéros de
 * ligne sont ceux du fichier de l'apprenant.
 *
 * Un `<script src>` vers un fichier inconnu est laissé tel quel : il ne charge
 * rien, comme dans un vrai navigateur devant un chemin cassé. Et une page sans
 * balise script n'exécute aucun script, même si l'onglet script.js existe :
 * c'est exactement ce que la leçon « où vit le script » doit laisser voir.
 */
// La garde des boucles, posée sur un fichier.
function garder(brut) {
    const garde = protegerBoucles(brut);
    return garde === brut ? brut : garde + GARDE;
}

/**
 * Un `<script type="module" src>` (section 23) devient `__repsModules(n)` :
 * js-page.js suit alors les `import` de proche en proche dans `modules`
 * (TOUS les scripts de la leçon, gardés), les réécrit en scripts classiques
 * (RepsMonde.module, js-console.js) et lance l'entrée.
 */
export function inlineScripts(fileMap, html) {
    const source = String(html == null ? "" : html);
    const scripts = [];
    let modules = null;
    const re = /<script\b([^>]*)>\s*<\/script\s*>/gi;
    const out = source.replace(re, (tout, attributs) => {
        const src = /\bsrc\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i.exec(attributs || "");
        const cible = src
            ? normalizeFileName(src[1] != null ? src[1] : src[2] != null ? src[2] : src[3])
            : "";
        if (!cible || !cible.endsWith(".js") || !fileMap.has(cible)) return tout;
        const brut = String(fileMap.get(cible) == null ? "" : fileMap.get(cible));
        // Chaque boucle reçoit sa garde (protegerBoucles.js) : sans elle, un
        // `while (true)` figeait l'onglet entier.
        if (/\btype\s*=\s*["']?module\b/i.test(attributs || "")) {
            if (!modules) {
                modules = {};
                for (const [nom, code] of fileMap) {
                    if (String(nom).endsWith(".js")) modules[nom] = garder(String(code == null ? "" : code));
                }
            }
            scripts.push({ nom: cible, code: modules[cible], module: true });
            return `<script>__repsModules(${scripts.length - 1})</script>`;
        }
        scripts.push({ nom: cible, code: garder(brut) });
        return `<script>__repsCharger(${scripts.length - 1})</script>`;
    });
    return { html: out, scripts, modules };
}

/**
 * Le document complet d'un aperçu vivant.
 *
 *   html     : la page déjà assemblée (feuilles de style résolues)
 *   fileMap  : les fichiers de la leçon (pour résoudre les scripts)
 *   canal    : jeton que la page recopie sur chacun de ses messages
 *   actions  : gestes à jouer avant la photo ([] pour l'aperçu que l'apprenant
 *              manipule lui-même)
 *   css      : la feuille de base de l'aperçu (celle de BrowserPane)
 *   head     : balises à glisser dans le <head> (la balise <base> des images)
 *   sources  : { console, page } le texte de js-console.js et de js-page.js
 *   boucle   : le message d'une boucle arrêtée (langue de la leçon)
 *   envoi    : le message d'un formulaire envoyé sans preventDefault()
 *   stockage : le localStorage de la page à son ouverture ({ clé: texte })
 */
export function liveDoc({ html, fileMap, canal, actions, css, head, sources, boucle, envoi, stockage }) {
    const { html: corps, scripts, modules } = inlineScripts(fileMap || new Map(), html);
    const reglages = {
        canal: String(canal || ""),
        actions: Array.isArray(actions) ? actions : [],
        fichiers: scripts,
    };
    if (modules) reglages.modules = modules;
    // Le localStorage de la page à son ouverture (section 19).
    if (stockage && typeof stockage === "object") reglages.stockage = stockage;
    // Le message d'une boucle arrêtée, dans la langue de la leçon.
    if (boucle) reglages.boucle = String(boucle);
    if (envoi) reglages.envoi = String(envoi);
    const amorce =
        `<meta charset="utf-8"><meta name="color-scheme" content="light">` +
        `<meta name="viewport" content="width=device-width,initial-scale=1">` +
        `<meta http-equiv="Content-Security-Policy" content="${LIVE_CSP}">` +
        String(head || "") +
        `<style>${String(css || "")}</style>` +
        `<script>window.__REPS_PAGE__=${JSON.stringify(reglages).replace(/</g, "\\u003c")};</script>` +
        `<script>${neutraliser(sources && sources.console)}\n</script>` +
        `<script>${neutraliser(sources && sources.page)}\n</script>`;

    if (/^\s*(?:<!doctype\b|<html\b)/i.test(corps)) {
        // Document complet écrit par la leçon : on glisse notre amorce au
        // début de SON <head>, sans l'envelopper dans un second squelette.
        const tete = /<head\b[^>]*>/i.exec(corps);
        const coupe = tete
            ? tete.index + tete[0].length
            : (/^\s*(<!doctype\b[^>]*>)?/i.exec(corps) || [""])[0].length;
        return corps.slice(0, coupe) + amorce + corps.slice(coupe);
    }
    return `<!doctype html><html><head>${amorce}</head><body>${corps}</body></html>`;
}

/** Les gestes d'une étape (`data.actions`), débarrassés de ce qui n'en est pas. */
export function actionsOf(data) {
    const brut = data && Array.isArray(data.actions) ? data.actions : [];
    return brut.filter(
        (a) =>
            a &&
            typeof a === "object" &&
            (typeof a.click === "string" ||
                typeof a.input === "string" ||
                typeof a.wait === "number" ||
                a.reload === true)
    );
}

/** Le localStorage de départ d'une étape (`data.storage`), en textes. */
export function storageOf(data) {
    const brut = data && data.storage && typeof data.storage === "object" ? data.storage : null;
    if (!brut) return {};
    const out = {};
    for (const k of Object.keys(brut)) out[k] = String(brut[k]);
    return out;
}
